import {
  onlyNumbersRegex,
  withoutSpecialCharsAndNumbersAllowDashRegex,
} from "apps/visits/packages/shared/static/regex";
import { parseYupDateString } from "apps/visits/packages/shared/utils/yup";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { getAdultDateOfBirthSchema, getAdultPeselSchema } from "@/core/utils/adulthood";
import { DATE_FORMAT } from "../../config/date-time";
import type { AgreementStates } from "../../types";
import { getAgreementsStates } from "../../utils/get-agreement-states";
import { getCheckboxRequiredSchema, safeBooleanSchema } from "../../utils/yup";

interface UseValidationSchemaProps {
  agreementStates: AgreementStates;
  isCustomAgreement?: boolean;
}

export const useValidationSchema = ({ agreementStates, isCustomAgreement }: UseValidationSchemaProps) => {
  const { t } = useTranslation();
  const { acceptGroupActionRequired, newsletterRequired, marketingPhoneContactRequired } =
    getAgreementsStates(agreementStates);
  return yup.object().shape({
    firstName: yup
      .string()
      .trim()
      .min(2, t("ERRORS.COMMONS.MINIMAL_LENGTH", { minimalLength: "2" }))
      .max(100, t("ERRORS.COMMONS.MAXIMAL_LENGTH", { maximalLength: "100" }))
      .required(t("ERRORS.COMMONS.REQUIRED"))
      .matches(withoutSpecialCharsAndNumbersAllowDashRegex, t("ERRORS.COMMONS.NOT_ALLOWED_CHARS")),
    lastName: yup
      .string()
      .trim()
      .min(2, t("ERRORS.COMMONS.MINIMAL_LENGTH", { minimalLength: "2" }))
      .max(100, t("ERRORS.COMMONS.MAXIMAL_LENGTH", { maximalLength: "100" }))
      .required(t("ERRORS.COMMONS.REQUIRED"))
      .matches(withoutSpecialCharsAndNumbersAllowDashRegex, t("ERRORS.COMMONS.NOT_ALLOWED_CHARS")),
    dialCode: yup.string().required(t("ERRORS.COMMONS.REQUIRED")),
    phone: yup
      .string()
      .required(t("ERRORS.COMMONS.REQUIRED"))
      .matches(onlyNumbersRegex, t("ERRORS.COMMONS.INCORRECT_PHONE_NUMBER"))
      .test("len", t("ERRORS.COMMONS.MINIMAL_LENGTH", { minimalLength: "8" }), (val: any) => val && val.length >= 8)
      .test("len", t("ERRORS.COMMONS.MAXIMAL_LENGTH", { maximalLength: "15" }), (val: any) => val && val.length <= 15),
    isDateOfBirth: safeBooleanSchema().default(false),
    // REV-100: sign-up is limited to adults on both identification paths.
    pesel: getAdultPeselSchema(t),
    dateOfBirth: yup.date().when("isDateOfBirth", {
      is: true,
      then: getAdultDateOfBirthSchema(t, DATE_FORMAT).required(t("ERRORS.COMMONS.REQUIRED")),
      otherwise: yup
        .date()
        .typeError(t("ERRORS.COMMONS.INCORRECT_DATE"))
        .transform((_, val) => parseYupDateString(_, val, DATE_FORMAT)),
    }),
    acceptDataProcessing: safeBooleanSchema()
      .oneOf([true], t("ERRORS.COMMONS.REQUIRED"))
      .required(t("ERRORS.COMMONS.REQUIRED")),
    acceptTouNPp: safeBooleanSchema()
      .oneOf([true], t("ERRORS.COMMONS.REQUIRED"))
      .required(t("ERRORS.COMMONS.REQUIRED")),
    acceptGroupAction: getCheckboxRequiredSchema(t, acceptGroupActionRequired),
    acceptNewsletter: getCheckboxRequiredSchema(t, newsletterRequired),
    acceptMarketingPhoneContact: getCheckboxRequiredSchema(t, marketingPhoneContactRequired),
    acceptCustomAgreement: isCustomAgreement
      ? safeBooleanSchema().oneOf([true], t("ERRORS.COMMONS.REQUIRED")).required(t("ERRORS.COMMONS.REQUIRED"))
      : yup.mixed().notRequired(),
  });
};
